import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError, type AiContentDetailResponse, type HelpdeskAiContentKind } from '@shared/api';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeaderRow,
  Heading,
  PageBackLink,
  Skeleton,
  Text,
} from '@shared/ui';
import { useAiContentDeletion } from '../model/use-ai-content-deletion';
import { AiContentDeletionFeedback } from './AiContentDeletionFeedback';
import { AiContentEditor } from './AiContentEditor';
import { AiContentError } from './AiContentError';
import { AiContentActions } from './AiContentActions';
import { AiContentHeader } from './AiContentHeader';
import { aiContentFormOptionsQuery } from '../api/queries';
import { aiContentTypeLabel } from '../model/presentation';
export function AiContentDetail({
  kind,
  scope,
  id,
  canUpdate,
  canDelete = false,
  onBack,
  startInEditMode = false,
  onEditEntryConsumed,
}: {
  kind: HelpdeskAiContentKind;
  scope: string;
  id: string;
  canUpdate: boolean;
  canDelete?: boolean;
  onBack: () => void;
  startInEditMode?: boolean;
  onEditEntryConsumed?: () => void;
}) {
  const { t } = useTranslation();
  const options = useQuery(aiContentFormOptionsQuery(scope, kind));
  const deletion = useAiContentDeletion({ scope, kind, id, canDelete, onDeleted: onBack });
  const { detail } = deletion;
  const readDenied = isApiError(detail.error) && [401, 403].includes(detail.error.status);
  const readMissing =
    isApiError(detail.error) && detail.error.status === 404 && !deletion.unresolvedDelete;
  const [editing, setEditing] = useState<AiContentDetailResponse>();
  const editEntryConsumed = useRef(false);
  useEffect(() => {
    if (
      !startInEditMode ||
      editEntryConsumed.current ||
      !canUpdate ||
      !detail.data ||
      Boolean(detail.data.data.deletion) ||
      detail.error ||
      detail.isFetching
    )
      return;
    editEntryConsumed.current = true;
    setEditing(detail.data);
    onEditEntryConsumed?.();
  }, [
    startInEditMode,
    canUpdate,
    detail.data,
    detail.error,
    detail.isFetching,
    onEditEntryConsumed,
  ]);
  if (editing)
    return (
      <AiContentEditor
        canUpdate={canUpdate && !readDenied && !readMissing && !detail.data?.data.deletion}
        kind={kind}
        scope={scope}
        initial={editing}
        onBack={onBack}
        onCancel={() => {
          setEditing(undefined);
        }}
        onSaved={() => {
          setEditing(undefined);
        }}
      />
    );
  const record = readDenied || readMissing ? undefined : detail.data?.data;
  const readFeedback =
    detail.error &&
    !(deletion.deleted && isApiError(detail.error) && detail.error.status === 404) ? (
      <AiContentError
        error={detail.error}
        loading={detail.isFetching}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    ) : null;
  const deletionFeedback = <AiContentDeletionFeedback deletion={deletion} canDelete={canDelete} />;
  const mutationBlocked = Boolean(
    record?.deletion || deletion.operationId || deletion.unresolvedDelete
  );
  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label={t(`aiContent.${kind}`)}>
      <PageBackLink onClick={onBack}>{t(`aiContent.back.${kind}`)}</PageBackLink>
      {detail.isPending ? (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton className="h-64 w-full" />
        </div>
      ) : record ? (
        <>
          <AiContentHeader
            record={record}
            typeLabel={aiContentTypeLabel(record.type, options.data, t('crm.missingValue'))}
            controls={
              canDelete ? (
                <AiContentActions
                  title={record.title}
                  disabled={
                    Boolean(detail.error) ||
                    detail.isFetching ||
                    deletion.pending ||
                    deletion.unresolvedDelete
                  }
                  onDelete={() => {
                    void deletion.beginDelete();
                  }}
                />
              ) : undefined
            }
          >
            {readFeedback}
            {deletionFeedback}
          </AiContentHeader>
          <Card>
            <CardContent className="min-w-0 space-y-3 p-4">
              <CardHeaderRow>
                <Heading as="h2" size="md">
                  {t('aiContent.information')}
                </Heading>
              </CardHeaderRow>
              <section className="space-y-2" aria-labelledby="ai-content-heading">
                <Text id="ai-content-heading" size="sm" color="fieldLabel">
                  {t('aiContent.fields.content')}
                </Text>
                <Text className="whitespace-pre-wrap break-words">
                  {record.content ?? t('crm.missingValue')}
                </Text>
              </section>
            </CardContent>
            {canUpdate && !mutationBlocked ? (
              <CardFooter className="flex-wrap justify-end gap-2 p-4 pt-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    Boolean(detail.error) ||
                    detail.isFetching ||
                    deletion.pending ||
                    deletion.unresolvedDelete
                  }
                  onClick={() => {
                    setEditing(detail.data);
                  }}
                >
                  <Pencil aria-hidden="true" />
                  {t('aiContent.edit')}
                </Button>
              </CardFooter>
            ) : null}
          </Card>
        </>
      ) : (
        <>
          {readFeedback}
          {deletionFeedback}
        </>
      )}
    </section>
  );
}
